import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import { FormikProps, useFormik } from "formik";
import { FC, useEffect } from "react";
import { connect } from "react-redux";
import { UnprocessableEntityError } from "../../../../errors";
import { useFlashMessage } from "../../../../hooks/useFlashMessage";
import AppStateInterface from "../../../../interfaces/AppStateInterface";
import KarteInterface from "../../../../interfaces/KarteInterface";
import OperatorInterface from "../../../../interfaces/OperatorInterface";
import { HTTP_ERROR_MESSAGE } from "../../../../reducers";
import styles from "./styles.scss";
import { useCreateKarteComment } from "./useCreateKarteComment";
import validationSchema from "./validationSchema";

interface Props {
  karte: KarteInterface;
  operator: OperatorInterface | null;
  studentId: string;
  sectionId: string;
}

interface Values {
  name: string;
  comment: string;
}

const PresentationalComponent: FC<Props> = ({
  karte,
  operator,
  studentId,
  sectionId,
}) => {
  const { showErrorMessage } = useFlashMessage();

  const { mutate: postComment, isPending } = useCreateKarteComment({
    studentId,
    karteId: karte.id,
    sectionId,
  });

  const formik = useFormik<Values>({
    initialValues: {
      name: operator ? operator.fullName : "",
      comment: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      postComment(values, {
        onSuccess: () => {
          resetForm();
        },
        onError: (error) => {
          if (
            error instanceof UnprocessableEntityError &&
            error.originalErrors.length > 0
          ) {
            showErrorMessage(
              error.originalErrors.map((e) => e.message).join("\n"),
            );
          } else {
            showErrorMessage(HTTP_ERROR_MESSAGE);
          }
        },
      });
    },
  });

  useEffect(() => {
    if (operator) {
      formik.setFieldValue("name", operator.fullName);
    }
  }, [operator]);

  const renderNameError = (formik: FormikProps<Values>) => {
    if (shouldDisplayError(formik, "name")) {
      return <p className={styles.error}>{formik.errors.name}</p>;
    } else {
      return null;
    }
  };

  const renderCommentError = (formik: FormikProps<Values>) => {
    if (shouldDisplayError(formik, "comment")) {
      return <p className={styles.error}>{formik.errors.comment}</p>;
    } else {
      return null;
    }
  };

  const isAnyFieldTouched = Object.values(formik.touched).some(Boolean);
  const isAnyFieldError = Object.values(formik.errors).some(Boolean);

  const shouldDisplayError = (
    formik: FormikProps<Values>,
    key: string,
  ): boolean => {
    const meta = formik.getFieldMeta(key);
    const { error } = meta;

    return isAnyFieldTouched && !!error;
  };

  const isDisabled =
    formik.isSubmitting || isPending || (isAnyFieldTouched && isAnyFieldError);

  return (
    <form
      className={styles.root}
      onSubmit={formik.handleSubmit}
      aria-label="カルテコメント投稿フォーム"
    >
      <div className={styles.nameField}>
        {renderNameError(formik)}
        <input
          className={classNames(styles.input, {
            [styles.fieldError]: shouldDisplayError(formik, "name"),
          })}
          name="name"
          onChange={formik.handleChange}
          value={formik.values.name}
          onBlur={formik.handleBlur}
        />
      </div>
      <div className={styles.commentField}>
        {renderCommentError(formik)}
        <textarea
          placeholder="コメントを入力"
          className={classNames(styles.textarea, {
            [styles.fieldError]: shouldDisplayError(formik, "comment"),
          })}
          rows={1}
          name="comment"
          onChange={formik.handleChange}
          value={formik.values.comment}
          onBlur={formik.handleBlur}
        />
      </div>
      <Button disabled={isDisabled} className={styles.button} type="submit">
        登録
      </Button>
    </form>
  );
};

const mapStateToProps = (state: AppStateInterface) => {
  return {
    operator: state.session.currentOperator,
  };
};

const KarteCommentForm = connect(mapStateToProps)(PresentationalComponent);

export default KarteCommentForm;
